import { userManager } from './oidcConfig';
import { getOperatorToken } from './betterAuthClient';

/**
 * Bearer token for requests that bypass the axios layer.
 *
 * SSE endpoints cannot go through axios: EventSource has no POST body and no
 * custom headers, so the migration runner and the log stream hand-roll `fetch`
 * against a ReadableStream. Each of those call sites originally read the token
 * straight from the OIDC `userManager` — which was correct until the portal
 * moved to Better Auth.
 *
 * After the cutover an operator signing in through Better Auth has no OIDC user
 * (empty token → 401) or, worse, a stale one left in storage from before the
 * migration, which authenticates as the old identity. The symptom is narrow and
 * easy to misread: ordinary pages work, because axios already prefers the
 * Better Auth token, while anything that streams silently fails to start.
 *
 * This mirrors the precedence in `api/axios.ts` exactly — Better Auth first,
 * OIDC only as a fallback — so there is one rule for what a request is
 * authenticated with, not one per transport.
 */
export async function getStreamAuthToken(): Promise<string> {
  const operatorToken = getOperatorToken();
  if (operatorToken) return operatorToken;

  try {
    const user = await userManager.getUser();
    if (user && !user.expired && user.access_token) return user.access_token;
  } catch {
    /* No OIDC user either — the caller sends an empty token and the server
       answers 401, which surfaces as a failed run rather than a silent hang. */
  }
  return '';
}
